/**
 * Tenant detail — the support view: status, quota/budget/storage standing,
 * job health, and lifecycle actions for one tenant.
 *
 * Campaign `auteur-cloud-commercial-v1`, task C_OPS.
 *
 * Every load and every lifecycle action on this page requires a `reason`
 * (rule U-5) — the server refuses without one (`400`), so the reason field is
 * not decoration, it is the request's only way to succeed. Nothing here is
 * ever pre-filled: a support conversation's justification belongs to that
 * conversation, not to a remembered default.
 *
 * **What this page never shows.** The usage report's `tokens` section carries
 * `durable: false` and its own explanation whenever it is present — this page
 * renders that note next to the figures rather than presenting them as a
 * persisted ledger. The `job_budget` section carries `available: false` until
 * C_QUOTA ships; this page renders that as "not available yet", never as a
 * blank meaning zero. Nothing here can render customer file content, a
 * provider key, or a receipt body, because the API this page reads never
 * returns one (see `admin_ops.py`'s module docstring).
 *
 * Build status: compiled and exercised as part of the Vite customer SPA.
 */

import React, { useCallback, useState } from 'react';

import {
  closeTenant,
  getTenant,
  getTenantAudit,
  getTenantUsage,
  reinstateTenant,
  suspendTenant,
} from './adminOpsApi';

function ReasonGate({ id, label, reason, setReason, onSubmit, busy, submitLabel }) {
  return (
    <form
      className="admin-ops-reason-gate"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
    >
      <label htmlFor={id}>{label}</label>
      <input id={id} type="text" value={reason} onChange={(event) => setReason(event.target.value)} />
      <button type="submit" disabled={busy || !reason.trim()}>
        {submitLabel}
      </button>
    </form>
  );
}

function StorageStanding({ storage }) {
  if (!storage) return null;
  if (storage.policy_state === 'missing') {
    return <p className="admin-ops-storage-missing">No active storage quota policy for this tenant.</p>;
  }
  return (
    <dl className="admin-ops-storage-standing">
      <dt>Plan</dt>
      <dd>{storage.quota && storage.quota.plan_code}</dd>
      <dt>Bytes used</dt>
      <dd>{storage.usage && storage.usage.byte_total}</dd>
      <dt>Objects</dt>
      <dd>{storage.usage && storage.usage.object_count}</dd>
      <dt>Writes allowed</dt>
      <dd>{storage.writes_allowed ? 'Yes' : 'No'}</dd>
    </dl>
  );
}

function JobHealth({ jobs }) {
  if (!jobs) return null;
  const recent = jobs.recent_errors || {};
  return (
    <div className="admin-ops-job-health">
      <dl>
        <dt>Queue depth</dt>
        <dd>{jobs.queue_depth}</dd>
      </dl>
      <table className="admin-ops-job-counts">
        <tbody>
          {Object.entries(jobs.counts || {}).map(([status, count]) => (
            <tr key={status}>
              <td>{status}</td>
              <td>{count}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <h4>Recent errors</h4>
      {(recent.dead_lettered || []).length === 0 && (recent.retrying || []).length === 0 ? (
        <p>No recent job errors.</p>
      ) : (
        <ul>
          {[...(recent.dead_lettered || []), ...(recent.retrying || [])].map((job) => (
            <li key={job.job_id}>
              {job.job_type} ({job.status}, attempt {job.attempts}/{job.max_attempts}):{' '}
              {job.last_error}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function TokenStanding({ tokens }) {
  if (!tokens) return null;
  if (!tokens.available) {
    return <p className="admin-ops-tokens-unavailable">Token usage: {tokens.reason}</p>;
  }
  return (
    <div className="admin-ops-token-standing">
      <p className="admin-ops-durability-note">{tokens.durability_note}</p>
      <dl>
        <dt>Calls observed (this process)</dt>
        <dd>{tokens.call_count}</dd>
        <dt>Estimated spend (USD)</dt>
        <dd>{tokens.estimated_complete ? tokens.estimated_usd : 'withheld — incomplete'}</dd>
        <dt>Actual/billed spend (USD)</dt>
        <dd>{tokens.actual_complete ? tokens.actual_usd : 'withheld — incomplete'}</dd>
      </dl>
    </div>
  );
}

export default function TenantDetailPage({ orgId }) {
  const [loadReason, setLoadReason] = useState('');
  const [detail, setDetail] = useState(null);
  const [usage, setUsage] = useState(null);
  const [auditReason, setAuditReason] = useState('');
  const [auditEvents, setAuditEvents] = useState(null);
  const [actionReason, setActionReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const load = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const payload = await getTenant(orgId, { reason: loadReason });
      setDetail(payload);
      setUsage(payload.usage);
    } catch (failure) {
      setError(failure.message);
    } finally {
      setBusy(false);
    }
  }, [orgId, loadReason]);

  const refreshUsage = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const payload = await getTenantUsage(orgId, { reason: loadReason });
      setUsage(payload.usage);
    } catch (failure) {
      setError(failure.message);
    } finally {
      setBusy(false);
    }
  }, [orgId, loadReason]);

  const loadAudit = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const payload = await getTenantAudit(orgId, { reason: auditReason });
      setAuditEvents(payload.events);
    } catch (failure) {
      setError(failure.message);
    } finally {
      setBusy(false);
    }
  }, [orgId, auditReason]);

  const runLifecycleAction = useCallback(
    async (action, successMessage) => {
      setBusy(true);
      setError(null);
      setNotice(null);
      try {
        const result = await action(orgId, { reason: actionReason });
        setNotice(successMessage(result));
        setActionReason('');
        await load();
      } catch (failure) {
        setError(failure.message);
      } finally {
        setBusy(false);
      }
    },
    [orgId, actionReason, load],
  );

  return (
    <section className="admin-ops-tenant-detail">
      <header>
        <h1>Tenant {orgId}</h1>
      </header>

      {error ? (
        <p className="admin-ops-error" role="alert">
          {error}
        </p>
      ) : null}
      {notice ? (
        <p className="admin-ops-notice" role="status">
          {notice}
        </p>
      ) : null}

      <ReasonGate
        id="admin-ops-load-reason"
        label="Reason for accessing this tenant's support view"
        reason={loadReason}
        setReason={setLoadReason}
        onSubmit={load}
        busy={busy}
        submitLabel="Load"
      />

      {detail ? (
        <>
          <dl className="admin-ops-tenant-summary">
            <dt>Status</dt>
            <dd>{detail.tenant.status}</dd>
            <dt>Slug</dt>
            <dd>{detail.tenant.slug}</dd>
            <dt>Created</dt>
            <dd>{detail.tenant.created_at}</dd>
            <dt>Closed</dt>
            <dd>{detail.tenant.closed_at || '—'}</dd>
          </dl>

          <h2>Projects</h2>
          {detail.projects.length === 0 ? (
            <p>No projects.</p>
          ) : (
            <ul>
              {detail.projects.map((project) => (
                <li key={project.project_id}>
                  {project.slug} ({project.status})
                </li>
              ))}
            </ul>
          )}

          <h2>Quota / budget / storage standing</h2>
          {usage ? (
            <>
              <h3>Storage</h3>
              <StorageStanding storage={usage.storage} />
              <h3>Jobs</h3>
              <JobHealth jobs={usage.jobs} />
              <h3>Tokens</h3>
              <TokenStanding tokens={usage.tokens} />
              <h3>Job budget</h3>
              <p className="admin-ops-job-budget">
                {usage.job_budget && usage.job_budget.available
                  ? 'Configured'
                  : `Not available — ${usage.job_budget && usage.job_budget.reason}`}
              </p>
              <button type="button" onClick={refreshUsage} disabled={busy}>
                Refresh usage
              </button>
            </>
          ) : null}

          <h2>Lifecycle actions</h2>
          <input
            aria-label="Reason for this lifecycle action"
            placeholder="Reason (required, recorded in the audit trail)"
            value={actionReason}
            onChange={(event) => setActionReason(event.target.value)}
          />
          <div className="admin-ops-lifecycle-actions">
            <button
              type="button"
              disabled={busy || !actionReason.trim() || detail.tenant.status !== 'active'}
              onClick={() =>
                runLifecycleAction(suspendTenant, (result) => `Tenant suspended (${result.tenant.status}).`)
              }
            >
              Suspend
            </button>
            <button
              type="button"
              disabled={busy || !actionReason.trim() || detail.tenant.status !== 'suspended'}
              onClick={() =>
                runLifecycleAction(
                  reinstateTenant,
                  (result) => `Tenant reinstated (${result.tenant.status}).`,
                )
              }
            >
              Reinstate
            </button>
            <button
              type="button"
              disabled={busy || !actionReason.trim() || detail.tenant.status === 'closed'}
              onClick={() =>
                runLifecycleAction(
                  closeTenant,
                  (result) =>
                    `Tenant closed. ${result.closure.project_count} project(s) exported first.`,
                )
              }
            >
              Close with export
            </button>
          </div>

          <h2>Audit trail</h2>
          <ReasonGate
            id="admin-ops-audit-reason"
            label="Reason for viewing this tenant's audit trail"
            reason={auditReason}
            setReason={setAuditReason}
            onSubmit={loadAudit}
            busy={busy}
            submitLabel="Load audit trail"
          />
          {auditEvents ? (
            auditEvents.length === 0 ? (
              <p>No audit events yet.</p>
            ) : (
              <table className="admin-ops-audit-table">
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Actor</th>
                    <th>Action</th>
                    <th>Decision</th>
                    <th>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {auditEvents.map((event) => (
                    <tr key={event.event_id} data-decision={event.decision}>
                      <td>{event.occurred_at}</td>
                      <td>{event.principal_id}</td>
                      <td>{event.action}</td>
                      <td>{event.decision}</td>
                      <td>{event.reason_detail}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          ) : null}
        </>
      ) : null}
    </section>
  );
}
